import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';

import { cooperplaceApi } from 'services/api';
import { useSnackbar } from 'v3/components/Snackbar';
import Text from 'v3/components/Text';

import { useClient } from '../shared/context';
import SharedData from '../shared/Section';
import Inputs from '../shared/Tracking/index';
import Editable from '../shared/Editable';
import { validate } from './validator';

function buildTrackingPayload(clientData) {
  const tracking = clientData.client || {};

  return {
    sending_tracking: tracking.sending_tracking,
    emails_tracking: tracking.emails_tracking || null,
    cost_centers: tracking.cost_centers?.map((item) => item?.id ?? item) || [],
    total_send_email: tracking.total_send_email?.id || null,
    time_interval: tracking.time_interval || [],
    enable_logic_panel: tracking.enable_logic_panel,
  };
}

export default function Tracking() {
  const snackbar = useSnackbar();
  const [isEditing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAllCostCenters, setShowAllCostCenters] = useState(false);
  const [showAllEmails, setShowAllEmails] = useState(false);

  const client = useClient();
  const { data, isFetching, canEditData } = client;

  async function saveData() {
    setLoading(true);

    const payload = buildTrackingPayload(client.data);
    const isValid = await validate(payload, client.setErrors);

    if (!isValid) {
      snackbar.show(<Text>Verifique os campos</Text>, {
        type: 'error',
      });
      setLoading(false);
      return;
    }

    try {
      const response = await cooperplaceApi.put(
        `clients/tracking/${client.data.id}/emails`,
        payload,
      );

      if (response.data?.success !== false) {
        client.setTrackingEmailsList([]);
        client.setPrevClient(client.data);
        snackbar.show(<Text>Salvo com sucesso</Text>, { type: 'success' });
        setEditing(false);
      } else {
        client.setTrackingEmailsList([]);
        client.setData(client.prevClient);
        snackbar.show(<Text>{response.data?.message || 'Erro ao salvar'}</Text>, {
          type: 'warning',
        });
        setEditing(false);
      }
    } catch (error) {
      snackbar.show(
        <Text>
          {error?.response?.data?.message || 'Erro ao salvar'}
        </Text>,
        { type: 'error' },
      );
    } finally {
      setLoading(false);
    }
  }

  const trackingMapValue =
    data?.client?.sending_tracking === false
      ? 'Não'
      : data?.client?.sending_tracking === true
        ? 'Sim'
        : 'Não informado';

  return (
    <SharedData
      Title="Envio de Tracking"
      HeaderRightComponent={
        canEditData && (
          <Editable
            isEditing={isEditing}
            setEditing={setEditing}
            onSave={saveData}
            isLoading={loading}
          />
        )
      }
      loading={isFetching}
    >
      {isEditing ? (
        <Inputs />
      ) : (
        <Row>
          <Col xs={12} md={6}>
            <Text type="label" color="dark">
              Recebe Tracking
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {trackingMapValue}
            </Text>
          </Col>
          <Col xs={12} md={6}>
            <Text type="label" color="dark">
              Mostrar no Painel Logístico
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {(client?.data?.client?.emails_tracking &&
                (client?.data?.client?.emails_tracking).split(',').map((item) => {
                  return (
                    <Text type="regular" color="gray">
                      {item}
                      <br />
                    </Text>
                  );
                })) ||
                'Não informado'}
            </Text>
          </Col>
          {
            data?.client?.sending_tracking === true && (
              <>
                <Col xs={12} md={12}>
                  <Text type="label" color="dark">
                    E-mails cadastrados
                  </Text>

                  {data?.client?.emails_tracking ? (
                    <>
                      <div
                        style={{
                          maxHeight: showAllEmails ? '300px' : '150px',
                          overflowY: showAllEmails ? 'auto' : 'hidden',
                          transition: 'max-height 0.3s ease',
                          borderRadius: '8px',
                          paddingRight: '8px',
                        }}
                      >
                        {data.client.emails_tracking
                          .split(',')
                          .map((email, idx) => (
                            <Text key={idx} type="regular" color="gray">
                              {email.trim()}
                              <br />
                            </Text>
                          ))}
                      </div>

                      {data.client.emails_tracking.split(',').length > 5 && (
                        <button
                          type="button"
                          className="btn btn-link p-0 mt-1"
                          style={{ fontSize: 'inherit' }}
                          onClick={() => setShowAllEmails(!showAllEmails)}
                        >
                          {showAllEmails
                            ? 'Ver menos'
                            : `Ver mais (${data.client.emails_tracking.split(',').length - 5})`}
                        </button>
                      )}
                    </>
                  ) : (
                    <Text type="regular" color="gray" as="p" className="mt-2">
                      Não informado
                    </Text>
                  )}
                </Col>
                <Col xs={12} md={12} className='mt-3'>
                  <Text type="label" color="dark">
                    Centros de Custo
                  </Text>
                  <Text type="regular" color="gray" as="p" className="mt-2">
                    {(data?.client?.cost_centers && data.client.cost_centers.length > 0)
                      ? (
                        <>
                          <div
                            style={{
                              maxHeight: showAllCostCenters ? '300px' : '180px',
                              overflowY: showAllCostCenters ? 'auto' : 'hidden',
                              transition: 'max-height 0.3s ease',
                              borderRadius: '8px',
                              paddingRight: '8px',
                            }}
                          >
                            {data.client.cost_centers.map((cc, idx) => (
                              <Text key={cc.id || idx} type="regular" color="gray">
                                {cc.name}
                                <br />
                              </Text>
                            ))}
                          </div>

                          {data.client.cost_centers.length > 10 && (
                            <button
                              type="button"
                              className="btn btn-link p-0 mt-1"
                              style={{ fontSize: 'inherit' }}
                              onClick={() => setShowAllCostCenters(!showAllCostCenters)}
                            >
                              {showAllCostCenters ? 'Ver menos' : `Ver mais (${data.client.cost_centers.length - 10})`}
                            </button>
                          )}
                        </>
                      )
                      : 'Não informado'}
                  </Text>
                </Col>
                <Col xs={12} md={6}>
                  <Text type="label" color="dark">
                    Total de envios diários
                  </Text>
                  <Text type="regular" color="gray" as="p" className="mt-2">
                    {data?.client?.time_interval?.length || 'Não informado'}
                  </Text>
                </Col>
                {data?.client?.time_interval?.length > 0 && (
                  <Col xs={12} md={12} className="mt-3">
                    <Text color="dark" type="label">
                      Horários de envio
                    </Text>
                    <Text type="regular" color="gray" as="p" className="mt-2">
                      {Array.isArray(data?.client?.time_interval) && data.client.time_interval.length > 0
                        ? data.client.time_interval.map((time, idx) => (
                          <Text key={idx} type="regular" color="gray">
                            {time}
                            <br />
                          </Text>
                        ))
                        : 'Não informado'}
                    </Text>
                  </Col>
                )}
              </>
            )
          }
        </Row>
      )}
    </SharedData>
  );
}
